import React, { Component } from 'react';
import { compose } from 'redux';
import { FormattedMessage, injectIntl } from '../../util/reactIntl';
import { Field, Form as FinalForm } from 'react-final-form';
import isEqual from 'lodash/isEqual';
import classNames from 'classnames';
import { ensureCurrentUser } from '../../util/data';
import * as validators from '../../util/validators';
import { Form, Avatar, Button, ImageFromFile, IconSpinner, FieldTextInput, LocationAutocompleteInputField, FieldSelect, CheckoutCustomFields } from '../../components';
import config from '../../config';

import css from './InvoiceSettingsForm.css';

const UPLOAD_CHANGE_DELAY = 2000; // Show spinner so that browser has time to load img srcset

// taxIdValidator(message)(value) -> undefined | message
const taxIdValidator = (message = 'error') => (value) => {
  if (typeof value !== 'string') return message;

  // Przygotowanie: obetnij spacje na brzegach
  const raw = value.trim();

  // 1) Musi się zaczynać co najmniej dwoma literami (kod kraju)
  const match = raw.match(/^([A-Za-z]{2})(.*)$/);
  if (!match) return 'NIP / VAT ID musi zaczynać się od kodu kraju (2 litery).';

  const cc = match[1].toUpperCase();   // kod kraju (2 litery)
  const restRaw = match[2];
  const rest = restRaw.replace(/\s+/g, ''); // usuń wewnętrzne spacje z reszty

  // Lista kodów krajów UE (ISO-2). Dla Grecji uwzględniamy także "EL".
  const EU_CODES = new Set([
    'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'EL',
    'HU', 'IE', 'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE'
  ]);

  // Walidacja NIP (PL): 10 cyfr + poprawna suma kontrolna
  const isValidNIP = (nip) => {
    if (!/^\d{10}$/.test(nip)) return false;
    const w = [6, 5, 7, 2, 3, 4, 5, 6, 7];
    const digits = nip.split('').map(Number);
    const sum = w.reduce((acc, weight, i) => acc + weight * digits[i], 0);
    const control = sum % 11;
    return control !== 10 && control === digits[9];
  };

  // Zasady:
  if (cc === 'PL') {
    // PL -> reszta musi być poprawnym NIP-em
    if (!isValidNIP(rest)) return 'Niepoprawny NIP';
    return undefined;
  }

  if (EU_CODES.has(cc)) {
    // Inny kraj UE -> reszta musi pasować do ^([0-9]|[A-Z]|\+|\*){1,12}$
    if (!/^[0-9A-Z+*]{1,12}$/.test(rest)) return 'Niepoprawny VAT ID dla kraju UE';
    return undefined;
  }

  // Poza UE i nie PL -> reszta musi mieć co najmniej 1 znak
  if (rest.length < 1) return 'Niepoprawny VAT ID';

  return undefined;
};

const INVOICE_FIELDS = [
  {
    "label": "StripePaymentForm.city",
    "type": "text",
    "placeholder": "StripePaymentForm.city",
    "name": "invoice.city",
    "required": true
  },
  {
    "label": "StripePaymentForm.zip",
    "type": "text",
    "placeholder": "StripePaymentForm.zip",
    "name": "invoice.zip",
    "required": true
  },
  {
    "label": "StripePaymentForm.address",
    "type": "text",
    "placeholder": "StripePaymentForm.address",
    "name": "invoice.address",
    "required": true
  },
];

const COMPANY_FIELDS = [
  {
    "label": "StripePaymentForm.companyName",
    "type": "text",
    "placeholder": "StripePaymentForm.companyName",
    "name": "invoice.companyName",
    "required": true
  },
  {
    "label": "StripePaymentForm.taxId",
    "type": "text",
    "placeholder": "StripePaymentForm.taxId",
    "name": "invoice.taxId",
    "required": true,
    "fieldValidator": validators.taxIdValidator,
    "fieldTransformer": value => value.trim().toUpperCase().replace(/\s+/g, '')
  },
  ...INVOICE_FIELDS
];

const INDIVIDUAL_FIELDS = [
  {
    "label": "StripePaymentForm.customerName",
    "type": "text",
    "placeholder": "StripePaymentForm.customerName",
    "name": "invoice.name",
    "required": true
  },
  ...INVOICE_FIELDS
];

class ProfileSettingsFormComponent extends Component {
  constructor(props) {
    super(props);

    this.uploadDelayTimeoutId = null;
    this.state = { uploadDelay: false };
    this.submittedValues = {};
  }

  render() {
    const displayCompanySection = false;
    return (
      <FinalForm
        {...this.props}
        render={fieldRenderProps => {
          const {
            className,
            currentUser,
            handleSubmit,
            intl,
            invalid,
            profileImage,
            rootClassName,
            updateInProgress,
            updateProfileError,
            uploadImageError,
            uploadInProgress,
            form,
            values,
          } = fieldRenderProps;

          const user = ensureCurrentUser(currentUser);

          const submitError = updateProfileError ? (
            <div className={css.error}>
              <FormattedMessage id="ProfileSettingsForm.updateProfileFailed" />
            </div>
          ) : null;

          const classes = classNames(rootClassName || css.root, className);
          const submitInProgress = updateInProgress;
          const submittedOnce = Object.keys(this.submittedValues).length > 0;
          const pristineSinceLastSubmit = submittedOnce && isEqual(values, this.submittedValues);
          const submitDisabled =
            invalid || submitInProgress;

          const COUNTRY_FIELD = [
            {
              "label": "StripePaymentForm.country",
              "type": "select",
              "options": config.countries.map(country => ({ value: country.code, label: country.name })),
              "placeholder": "StripePaymentForm.country",
              "name": "invoice.countryCode",
              "required": true,
              "default": process.env.REACT_APP_COUNTRY_ID
            }
          ];

          return (
            <Form
              className={classes}
              onSubmit={e => {
                this.submittedValues = values;
                handleSubmit(e);
              }}
            >
              <div className={css.sectionContainer}>
                <div className={css.paymentSection}>
                  <FieldSelect
                    id="type"
                    name="customerType"
                    label={intl.formatMessage({ id: 'StripePaymentForm.customerType' })}
                    validate={validators.required('Type required')}
                  >
                    <option value="individual">
                      {intl.formatMessage({ id: 'StripePaymentForm.indivdual' })}
                    </option>
                    <option value="company">
                      {intl.formatMessage({ id: 'StripePaymentForm.company' })}
                    </option>
                  </FieldSelect>
                  <CheckoutCustomFields
                    additionalFields={{
                      fields:
                        values.customerType === 'company'
                          ? [...COUNTRY_FIELD, ...COMPANY_FIELDS].map(f => ({
                            ...f,
                            required: values.customerType === 'company',
                          }))
                          : [...COUNTRY_FIELD, ...INDIVIDUAL_FIELDS].map(f => ({
                            ...f,
                            required: values.customerType === 'individual',
                          })),
                    }}
                    intl={intl}
                    needsTranslation
                  />
                </div>
              </div>
              {submitError}
              <Button
                className={css.submitButton}
                type="submit"
                inProgress={submitInProgress}
                disabled={submitDisabled}
                ready={pristineSinceLastSubmit}
              >
                <FormattedMessage id="ProfileSettingsForm.saveChanges" />
              </Button>
            </Form>
          );
        }}
      />
    );
  }
}

ProfileSettingsFormComponent.defaultProps = {
  rootClassName: null,
  className: null,
  uploadImageError: null,
  updateProfileError: null,
  updateProfileReady: false,
};

const ProfileSettingsForm = compose(injectIntl)(ProfileSettingsFormComponent);

ProfileSettingsForm.displayName = 'ProfileSettingsForm';

export default ProfileSettingsForm;
